import { CommonModule } from "@angular/common";
import {
  Component,
  inject,
  Input,
  OnInit,
  signal,
  ViewChild,
} from "@angular/core";
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
} from "@angular/forms";
import { MatInputModule } from "@angular/material/input";
import { FUNCTION_RESULT_STATUS, IReference } from "@nova/common-interfaces";
import { ICustomerOverview, ReferenceKeyHelper } from "@rfm/shared-interfaces";
import {
  StripeCardElementOptions,
  StripeElementsOptions,
  StripePaymentElementOptions,
} from "@stripe/stripe-js";
import {
  injectStripe,
  StripeElementsDirective,
  StripePaymentElementComponent
} from "ngx-stripe";
import { ButtonModule } from "primeng/button";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { InputNumberModule } from "primeng/inputnumber";
import { ToggleSwitchModule } from "primeng/toggleswitch";
import { BehaviorSubject, Observable } from "rxjs";
import { environment } from "../../../../../../environments/environment";
import { CustomerService } from "../../../../../shared/services/customers/customers.service.mjs";
import { PaymentService } from "../../../../../shared/services/financial/payment.service.mjs";
import { ThirdPartyStripeService } from "../../../../../shared/services/third-party/stripe/third-party-stripe.service";

@Component({
  selector: "app-stripe-payment-methods",
  templateUrl: "./stripe-payment-methods.component.html",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    InputNumberModule,
    ToggleSwitchModule,
    StripePaymentElementComponent,
    ButtonModule,
    StripeElementsDirective
  ],
})
export class ThirdpartyStripePaymentMethodsComponent implements OnInit {
  // Replace with your own public key
  stripe = injectStripe(environment.stripeApiKey);

  private _isLoading = new BehaviorSubject<boolean>(false);
  isLoading$: Observable<boolean> = this._isLoading.asObservable();

  @ViewChild(StripePaymentElementComponent)
  paymentElement!: StripePaymentElementComponent;

  elementsOptions: StripeElementsOptions = {
    locale: 'en',
    appearance: {
      theme: 'night'
    }
  };

  paymentElementOptions: StripePaymentElementOptions = {
    layout: {
      type: 'tabs',
      defaultCollapsed: false,
      radios: false,
      spacedAccordionItems: false,
    }
  };
  
  cardElementOptions: StripeCardElementOptions = {    layout: {
      type: 'tabs',
      defaultCollapsed: false,
      radios: false,
      spacedAccordionItems: false,
    }
} as StripeCardElementOptions;

  @Input()
  customer_uid = signal("");

  customer_overview: ICustomerOverview | undefined;

  toggleSwitchChanged(event: any) {
    console.log(event);
  }

  constructor(
    protected thirdPartyStripeService: ThirdPartyStripeService,
    config: DynamicDialogConfig,
    protected ref: DynamicDialogRef,
    protected customerService: CustomerService,
    protected paymentService: PaymentService
  ) {
    this.customer_uid.set(config.data["uid"]);
  }

  ngOnInit(): void {
    this._isLoading.next(true);

    this.customerService
      .getOverview(this.customer_uid())
      .then(async (result) => {
        if (result.status !== FUNCTION_RESULT_STATUS.SUCCESS) {
          console.log("ERROR GETTING CUSTOMER FOR STRIPE PAYMENT");
          return Promise.reject("ERROR GETTING CUSTOMER");
        }

        this.customer_overview = result.data!;

        let stripeId = ReferenceKeyHelper.getId(
          "stripe",
          this.customer_overview!.reference_keys.data
        );

        if (!stripeId)
        {
          const stripeCustomer = await this.thirdPartyStripeService.createCustomer(this.customer_overview.id, this.customer_overview.name!);

          if (stripeCustomer.status !== FUNCTION_RESULT_STATUS.SUCCESS)
          {
            throw new Error("Unable to create customer");
          }

          stripeId = stripeCustomer.data?.id;

          this.customer_overview.reference_keys.data.push({
            id: stripeId,
            name: "stripe",
            referenceType: "EXTERNAL",
            referenceId: stripeId,
            createdAt: Date.now(),
            createdBy: "UI"
          } as IReference)

          if (!this.customer_overview.total_balance)
          {
            this.customer_overview.total_balance = 0;
          }
          if (!this.customer_overview.total_subscriptions)
          {
            this.customer_overview.total_subscriptions = 0;
          }
          if (!this.customer_overview.last_payment_attempt)
          {
            this.customer_overview.last_payment_attempt = 0;
          }
          const customerResult = await this.customerService.update(this.customer_overview);

          if (customerResult.status !== FUNCTION_RESULT_STATUS.SUCCESS)
          {
            throw new Error("Unable to create customer");
          }
        }

        console.log("STRIPE ID", stripeId);

        this.thirdPartyStripeService
          .setupPaymentIntent(stripeId!)
          .then((result) => {
            if (result.status !== FUNCTION_RESULT_STATUS.SUCCESS) {
              console.error("GETTING CLIENT SECRET FAILED", result);
              return Promise.reject("UNABLE TO GET CLIENT SECRET");
            }
            console.log(result.data);
        this.elementsOptions.clientSecret = result.data!.client_secret!;
            return Promise.resolve();
          })
          .finally(() => {
            this._isLoading.next(false);
          });
      });
  }

  fb = inject(UntypedFormBuilder);

  async addCard() {
    try {
      this.stripe!.confirmSetup({
        elements: this.paymentElement.elements,
        redirect: "if_required", // Only redirects if 3D Secure/OTP is needed
      }).subscribe((result) => {
        if (result.error) {
          console.log("Error:", result.error.message);
        } else if (result.setupIntent.status === "succeeded") {
          // Success! The payment method is now linked to the Customer in Stripe
          console.log("Payment method saved successfully!");
          this.ref.close(result);
        }
      });
    } catch (error: any) {
      console.error(error);
      this.ref.close(undefined);
    }
  }

  cancel() {
    this.ref.close();
  }
}
